/**
 * Evidence Contract Types
 * 
 * Implements the 3-Tier Disclosure Model for IP protection:
 * - MEMBER: Score + trend + coaching tips (public)
 * - LENDER: Above + factor scores + summary counts (authenticated)
 * - AUDITOR: Full disclosure including formulas/weights (NDA required)
 * 
 * @see WORKPLAN-LENDER-AUDIT-TRACEABILITY-MATRIX.md
 */

// =============================================================================
// DISCLOSURE LEVELS (IP Protection)
// =============================================================================

export type DisclosureLevel = 'MEMBER' | 'LENDER' | 'AUDITOR';

export interface UserDisclosureContext {
  level: DisclosureLevel;
  userId: string;
  role: 'member' | 'lender' | 'auditor' | 'admin';
  fiId?: string;           // For lenders: their FI ID
  ndaSignedAt?: string;    // For auditors: must be set
}

// =============================================================================
// EVIDENCE CONTRACT (Core Schema)
// =============================================================================

export interface EvidenceContract {
  // Always shown (all disclosure levels)
  run_id: string;
  metric_key: MetricKey;
  label: string;
  value: number;
  unit: 'USD' | 'PERCENT' | 'COUNT' | 'RATIO' | 'MONTHS';
  period: MetricPeriod;
  as_of: string; // ISO timestamp

  // Summary (MEMBER+)
  summary: EvidenceSummary;

  // Run metadata (LENDER+ only) - supports Audit Q2.1, Q2.3
  run_metadata?: RunMetadata;

  // Components breakdown (LENDER+ only)
  components?: EvidenceComponents;

  // Full calculation details (AUDITOR only)
  calculation?: EvidenceCalculation;

  // Source evidence (AUDITOR only)
  sources?: EvidenceSources;

  // Integrity verification (AUDITOR only)
  integrity?: EvidenceIntegrity;

  // BUG-UI-061: Raw intermediate_metrics from API for income_basis access
  // Contains income_basis, monthly_breakdown, and other calculation metadata
  intermediate_metrics?: Record<string, unknown>;
}

// Run metadata - answers Audit Q2.1, Q2.2, Q2.3, Q5.1
// Epic 65M: Extended with confidence and basis tracking
export interface RunMetadata {
  // Algorithm version info (Q2.1)
  algorithm_version: string;
  git_sha: string;
  config_hash: string;

  // Data coverage (Q2.3)
  input_txn_count: number;
  input_account_count: number;
  input_months_coverage: number;

  // Snapshot references (Q2.2)
  plaid_snapshot_id?: string;
  crs_snapshot_id?: string;

  // Run status (Q5.1)
  status: 'SUCCESS' | 'FAIL' | 'PARTIAL';
  execution_time_ms?: number;
  run_ts: string;

  // Epic 65M: Confidence tracking
  confidence_score?: number; // 0-100
  confidence_tier?: 'HIGH' | 'MEDIUM' | 'LOW'; // Derived from confidence_score
  confidence_reasons?: string[]; // Reason codes explaining confidence

  // Epic 65M: Income basis tracking
  income_basis_used?: 'VERIFIED_GROSS' | 'DEPOSITED_NET' | 'ATTESTED' | 'HYBRID';
  income_basis_reason_code?: string;

  // Epic 65M: Debt basis tracking
  debt_basis_used?: 'CRS_BUREAU' | 'PLAID_LIABILITIES' | 'ATTESTED' | 'HYBRID';
  debt_basis_reason_code?: string;

  // Epic 65M: Coverage tracking
  coverage_months_available?: number;
  coverage_months_used?: number;
  data_freshness_ts?: string; // ISO timestamp
}

// =============================================================================
// SUB-TYPES
// =============================================================================

export interface MetricPeriod {
  type: 'MONTH' | 'AS_OF' | 'ROLLING_6M' | 'ROLLING_12M' | 'ROLLING_24M';
  start?: string;  // ISO date
  end?: string;    // ISO date
  as_of?: string;  // For point-in-time snapshots
}

export interface EvidenceSummary {
  display_value: string;           // Formatted: "$4,745.00"
  trend?: 'UP' | 'DOWN' | 'STABLE';
  trend_percent?: number;          // e.g., +5.2%
  coaching_tip?: string;           // FCRA-compliant reason code explanation
  one_liner: string;               // "Sum of monthly expenses from 143 transactions"
}

export interface EvidenceComponents {
  count: number;                   // "143 transactions" or "2 liabilities"
  breakdown_type: 'CATEGORY' | 'LIABILITY_TYPE' | 'ACCOUNT' | 'FACTOR' | 'METRIC' | 'MONTH';
  items: ComponentItem[];          // Category percentages (LENDER), full values (AUDITOR)
}

export interface ComponentItem {
  name: string;                    // "Groceries", "Credit Card", etc.
  percentage?: number;             // LENDER level: 45%
  value?: number;                  // AUDITOR level: $812.33
  count?: number;                  // Number of items in this category
  group?: string;                  // BUG-UI-023: Optional grouping (e.g., "Income Metrics", "Expense Metrics")
  formula?: string;                // BUG-UI-023: Optional formula for this component
  // Epic 62: Source badges and breakdown grouping
  source_badge?: 'ATTESTED' | 'DEPOSITED' | 'PLAID' | 'CRS' | 'HYBRID' | 'VERIFIED';
  breakdown_group?: 'INPUTS' | 'DERIVED' | 'RESULT';  // Epic 62: Inputs/Derived/Result grouping
  drilldown_data?: ComponentDrilldown;  // Epic 62 US62.2: Drilldown panel data
}

// Epic 62 US62.2: Drilldown data for components
export interface ComponentDrilldown {
  type: 'DEBT_PAYMENTS' | 'INCOME' | 'EXPENSES' | 'GENERIC';
  included_items?: DrilldownItem[];
  excluded_items?: DrilldownItem[];
  selection_rationale?: string;
  comparison_data?: {
    attested?: number;
    deposited?: number;
    verified?: number;
    selected: 'ATTESTED' | 'DEPOSITED' | 'VERIFIED';
    reason: string;
  };
}

export interface DrilldownItem {
  name: string;
  amount: number;
  source: 'ATTESTED' | 'DEPOSITED' | 'PLAID' | 'CRS' | 'HYBRID';
  confidence?: 'HIGH' | 'MEDIUM' | 'LOW';
  reason?: string;  // For excluded items
}

export interface EvidenceCalculation {
  formula: string;                 // "sum(abs(tx.amount)) for included debit tx"
  steps: CalculationStep[];
  config_ref: {
    ruleset_version: string;       // "tx_rules_v3.2"
    git_sha: string;               // "9f2c..."
    algorithm_version: string;     // "verascore_v2.3"
  };
  weights?: Record<string, number>; // THE SECRET SAUCE - factor weights
  thresholds?: Record<string, number>; // Rating thresholds
  // Epic 63: Mapping table for score calculation
  mapping_table?: MappingTableEntry[];
  // Epic 63: Calculation context
  calculation_context?: CalculationContext;
}

// Epic 63 US63.1: Mapping table for raw value → score conversion
export interface MappingTableEntry {
  range_label: string;             // "< 20%", "20-36%", etc.
  range_min?: number;              // Minimum value (undefined for open-ended)
  range_max?: number;              // Maximum value (undefined for open-ended)
  score_output: string;            // "100", "60-100 (linear)", etc.
}

// Epic 63 US63.2: Calculation context
export interface CalculationContext {
  income_window?: string;          // "6-month average, annualized"
  debt_window?: string;            // "Credit pull month (Dec 2025)"
  income_basis?: string;           // "Deposited [ATTESTED_MISSING]"
  debt_basis?: string;             // "Credit min payments [CRS_AVAILABLE]"
  snapshot_id?: string;            // "abc123def456..."
  normalization_rules_version?: string; // "v2.1"
}

export interface CalculationStep {
  order: number;
  name: string;
  detail: string;
  intermediate_value?: number;
}

export interface EvidenceSources {
  accounts?: AccountSource[];
  liabilities?: LiabilitySource[];
  transactions?: TransactionSources;
  bureau?: BureauSource;
}

export interface AccountSource {
  account_id: string;
  mask: string;                    // "1189"
  institution: string;
  type: 'depository' | 'credit' | 'loan' | 'investment';
  subtype?: string;
  balance_available?: number;
  balance_current?: number;
  balance_used: number;
  include_reason: string;
}

export interface LiabilitySource {
  liability_id: string;
  account_id: string;
  type: 'credit_card' | 'student_loan' | 'mortgage' | 'auto_loan' | 'personal_loan';
  min_payment: number;
  balance: number;
  due_date?: string;
  include_reason: string;
}

export interface TransactionSources {
  included_count: number;
  excluded_count: number;
  included: TransactionItem[];     // Full list (AUDITOR only)
  excluded: ExcludedTransaction[]; // With reasons (AUDITOR only)
}

export interface TransactionItem {
  tx_id: string;
  date: string;
  amount: number;
  merchant: string;
  category: string;
  direction: 'credit' | 'debit';
}

export interface ExcludedTransaction {
  tx_id: string;
  reason: ExclusionReason;
  amount?: number;
}

export type ExclusionReason = 
  | 'transfer_internal'
  | 'credit_card_payment'
  | 'loan_payment'
  | 'investment_transfer'
  | 'duplicate'
  | 'reversal'
  | 'out_of_window';

export interface BureauSource {
  bureau_report_id: string;
  pull_timestamp: string;
  score_model: string;
  score_model_version: string;
  tradeline_count: number;
}

export interface EvidenceIntegrity {
  inputs_hash: string;             // SHA256 of input data
  outputs_hash: string;            // SHA256 of calculated outputs
  verified: boolean;
  verification_timestamp: string;
}

// =============================================================================
// METRIC KEYS (All 39 UI Entities)
// =============================================================================

export type MetricKey =
  // Current Finances Section - Database entity names (CANONICAL_METRICS)
  | 'money_in_1m'              // Gross income / money in
  | 'money_out_1m'             // Monthly expenses / money out
  | 'money_leftover_1m'        // Money leftover
  | 'net_savings_1m'           // Net savings
  | 'cash_on_hand'             // Cash on hand
  | 'dti_ratio'                // Debt-to-income ratio
  | 'monthly_debt_payments'    // Minimum debt payments
  | 'total_debt'               // Outstanding debt
  | 'late_payments'            // Late payments count
  | 'credit_score'             // Credit score
  // Factor Scores - Database entity names (CANONICAL_FACTORS)
  | 'cash_flow_factor'
  | 'debt_to_income_factor'
  | 'liquid_reserves_factor'
  | 'savings_factor'
  | 'debt_management_factor'
  | 'payment_history_factor'
  // Rolling Averages - Database entity names
  | 'gross_income_6m'
  | 'gross_income_1m'
  | 'yearly_gross_income'
  | 'money_in_6m'
  | 'money_out_6m'
  | 'money_leftover_6m'
  // Legacy UI metric keys (for backward compatibility)
  | 'gross_income_month'
  | 'minimum_debt_payments_month'
  | 'cash_on_hand_as_of'
  | 'money_in_month'
  | 'money_leftover_month'
  | 'dti_ratio_month'
  | 'monthly_expenses_month'
  | 'net_savings_month'
  | 'outstanding_debt_as_of'
  | 'late_payments_12m'
  | 'credit_score_as_of'
  // Factor Card keys - legacy UI names
  | 'factor_income_stability'
  | 'factor_expense_management'
  | 'factor_savings_behavior'
  | 'factor_debt_management'
  | 'factor_credit_history'
  | 'factor_financial_stress'
  | 'factor_dti'
  | 'factor_cash_on_hand'
  | 'factor_spending'
  | 'factor_savings'
  | 'factor_debt'
  | 'factor_payment_history'
  // VeraScore (4 metrics)
  | 'verascore_current'
  | 'verascore_6m_avg'
  | 'verascore_12m_avg'
  | 'verascore_24m_avg'
  // Derived/Trend metrics
  | 'expense_to_income_ratio'
  | 'savings_rate'
  | 'debt_service_coverage'
  | 'liquidity_ratio'
  | 'income_trend'
  | 'expense_trend'
  | 'savings_trend'
  | 'score_trend';

// =============================================================================
// REDACTION UTILITY
// =============================================================================

/**
 * Redacts an EvidenceContract based on the user's disclosure level.
 *
 * TODO: RBAC - Re-enable tiered redaction when role-based security is implemented
 * For now, all levels get full disclosure during development/testing
 *
 * Original levels:
 * - MEMBER: Only summary (score + trend + coaching)
 * - LENDER: Summary + components (counts, categories %)
 * - AUDITOR: Full disclosure (formulas, weights, transactions)
 */
export function redactForLevel(
  evidence: EvidenceContract,
  level: DisclosureLevel
): EvidenceContract {
  // TODO: RBAC - Re-enable redaction when role-based security is implemented
  // For now, return full evidence regardless of level
  return evidence;

  /* ORIGINAL REDACTION LOGIC - Re-enable with RBAC:
  // Base fields always included
  const base: EvidenceContract = {
    run_id: evidence.run_id,
    metric_key: evidence.metric_key,
    label: evidence.label,
    value: evidence.value,
    unit: evidence.unit,
    period: evidence.period,
    as_of: evidence.as_of,
    summary: evidence.summary,
  };

  if (level === 'MEMBER') {
    return base;
  }

  if (level === 'LENDER') {
    return {
      ...base,
      run_metadata: evidence.run_metadata,
      components: evidence.components ? {
        count: evidence.components.count,
        breakdown_type: evidence.components.breakdown_type,
        items: evidence.components.items.map(item => ({
          name: item.name,
          percentage: item.percentage,
          count: item.count,
          value: item.value,
        })),
      } : undefined,
      calculation: evidence.calculation ? {
        formula: evidence.calculation.formula,
        steps: evidence.calculation.steps,
        config_ref: evidence.calculation.config_ref,
      } : undefined,
    };
  }

  // AUDITOR - full disclosure
  return evidence;
  */
}

/**
 * Determines disclosure level from user context.
 */
export function getDisclosureLevel(user: UserDisclosureContext): DisclosureLevel {
  if (user.role === 'auditor' && user.ndaSignedAt) {
    return 'AUDITOR';
  }
  if ((user.role === 'lender' || user.role === 'admin') && user.fiId) {
    return 'LENDER';
  }
  return 'MEMBER';
}

